import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import {
  TrendingUp, Users, Heart, Target, IndianRupee, FileText, Image as ImageIcon,
  MessageSquare, ShieldAlert, CalendarClock, Sparkles, Radio,
} from "lucide-react";
import LiveSocialMetrics from "@/components/LiveSocialMetrics";

const SENTIMENT_COLORS = { positive: "#10b981", neutral: "#6366F1", negative: "#f43f5e", unknown: "#64748b" };

const fmtINR = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v || 0);

export default function Analytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState("internal"); // "internal" | "live"

  useEffect(() => {
    api.get(`/dashboard/analytics?days=${days}`).then((r) => setData(r.data));
  }, [days]);

  if (!data) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-72 bg-white/5 rounded animate-pulse" />
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 bg-white/5 rounded-2xl animate-pulse" />
          ))}
        </div>
        <div className="h-72 bg-white/5 rounded-2xl animate-pulse" />
      </div>
    );
  }

  const k = data.kpis;
  const sentimentData = Object.entries(data.sentiment || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="space-y-8" data-testid="analytics-root">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2">Insights · Live data</div>
          <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Analytics</h1>
          <p className="text-white/50 mt-2 max-w-2xl text-sm">
            Real activity across your AI marketing engine — content output, inbox sentiment, CRM pipeline, and estimated value created.
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value, 10))}
          data-testid="analytics-period-select"
          className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </header>

      <div className="flex gap-2 border-b border-white/5" data-testid="analytics-tabs">
        <TabBtn active={tab === "internal"} onClick={() => setTab("internal")} testid="analytics-tab-internal">
          <TrendingUp className="h-3.5 w-3.5" /> Internal activity
        </TabBtn>
        <TabBtn active={tab === "live"} onClick={() => setTab("live")} testid="analytics-tab-live">
          <Radio className="h-3.5 w-3.5" /> Live social metrics
        </TabBtn>
      </div>

      {tab === "live" && <LiveSocialMetrics />}

      {tab === "internal" && (<>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-kpis">
        <Kpi icon={FileText} label="Content generated" value={k.total_content} delta={k.period_content} accent="indigo" />
        <Kpi icon={ImageIcon} label="Creatives" value={k.total_creatives} delta={k.period_creatives} accent="violet" />
        <Kpi icon={MessageSquare} label="Inbox sent" value={k.inbox_sent} sub={`${k.inbox_pending} pending`} accent="purple" />
        <Kpi icon={IndianRupee} label="Estimated value" value={fmtINR(k.estimated_value_inr)} accent="fuchsia" />
      </section>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={CalendarClock} label="Published posts" value={k.published_posts} sub={`${k.live_published} LIVE`} accent="emerald" />
        <Kpi icon={CalendarClock} label="Scheduled" value={k.scheduled_pending} accent="cyan" />
        <Kpi icon={Target} label="CRM open" value={k.crm_open} sub={`${k.crm_won} won`} accent="amber" />
        <Kpi icon={ShieldAlert} label="Escalated" value={k.inbox_escalated} sub={`${data.sensitive_count} sensitive`} accent="rose" />
      </section>

      <section className="grid lg:grid-cols-3 gap-6">
        <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="content-timeseries">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-black text-lg">Daily content output</h3>
            <span className="text-xs text-white/40 font-mono">{data.period_days} days</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.timeseries}>
              <defs>
                <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366F1" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} hide />
              <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} />
              <Tooltip contentStyle={{ background: "#0a0e1a", border: "1px solid rgba(255,255,255,0.1)" }} />
              <Area type="monotone" dataKey="content" stroke="#6366F1" strokeWidth={2} fill="url(#cg)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="glass rounded-2xl p-6" data-testid="sentiment-chart">
          <h3 className="font-heading font-black text-lg mb-4">Inbox sentiment</h3>
          {sentimentData.every((s) => s.value === 0) ? (
            <div className="text-sm text-white/40 text-center py-10">No inbox messages in this period.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={sentimentData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {sentimentData.map((s) => (
                    <Cell key={s.name} fill={SENTIMENT_COLORS[s.name] || "#64748b"} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#0a0e1a", border: "1px solid rgba(255,255,255,0.1)" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex flex-wrap gap-2 mt-2 text-xs">
            {sentimentData.map((s) => (
              <span key={s.name} className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full" style={{ background: SENTIMENT_COLORS[s.name] || "#64748b" }} />
                <span className="text-white/60 capitalize">{s.name}</span>
                <span className="text-white/40 font-mono">{s.value}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6" data-testid="platform-breakdown">
          <h3 className="font-heading font-black text-lg mb-4">Content by platform</h3>
          {(data.platform_breakdown || []).length === 0 ? (
            <div className="text-sm text-white/40 text-center py-10">No content yet — start with Power Hour.</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.platform_breakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="platform" tick={{ fontSize: 11, fill: "rgba(255,255,255,0.6)" }} />
                <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} />
                <Tooltip contentStyle={{ background: "#0a0e1a", border: "1px solid rgba(255,255,255,0.1)" }} />
                <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="glass rounded-2xl p-6" data-testid="publish-by-platform">
          <h3 className="font-heading font-black text-lg mb-4">Published by platform</h3>
          {(data.publish_by_platform || []).length === 0 ? (
            <div className="text-sm text-white/40 text-center py-10">Nothing published yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.publish_by_platform}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="platform" tick={{ fontSize: 11, fill: "rgba(255,255,255,0.6)" }} />
                <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} />
                <Tooltip contentStyle={{ background: "#0a0e1a", border: "1px solid rgba(255,255,255,0.1)" }} />
                <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      <section className="glass rounded-2xl p-6">
        <h3 className="font-heading font-black text-lg mb-4 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-indigo-400" /> AI insights
        </h3>
        <ul className="space-y-2 text-sm text-white/70">
          {k.inbox_escalated > 0 && (
            <li>• You have <strong className="text-rose-300">{k.inbox_escalated} escalated message(s)</strong> requiring human review. Check the Reply Inbox.</li>
          )}
          {k.scheduled_pending > 0 && (
            <li>• <strong className="text-indigo-300">{k.scheduled_pending} post(s)</strong> queued in the calendar.</li>
          )}
          {k.crm_open > 0 && (
            <li>• <strong className="text-amber-300">{k.crm_open} open lead(s)</strong> in your pipeline — try Power Hour to nurture them with fresh content.</li>
          )}
          {k.period_content === 0 && (
            <li>• No new content created in the last {data.period_days} days. Run ⚡ Power Hour to refill your queue.</li>
          )}
          {k.estimated_value_inr > 50000 && (
            <li>• AI has produced an estimated <strong className="text-emerald-300">{fmtINR(k.estimated_value_inr)}</strong> worth of marketing assets for you.</li>
          )}
        </ul>
      </section>
      </>)}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-t-lg border-b-2 transition ${
        active ? "border-indigo-500 text-white" : "border-transparent text-white/50 hover:text-white/80"
      }`}>
      {children}
    </button>
  );
}

function Kpi({ icon: Icon, label, value, delta, sub, accent }) {
  return (
    <div className="glass rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-[0.2em] text-white/40">{label}</span>
        <Icon className={`h-4 w-4 text-${accent}-400`} />
      </div>
      <div className="font-heading text-2xl font-black truncate">{value}</div>
      {delta !== undefined && delta > 0 && (
        <div className="text-xs text-emerald-300 mt-1">+{delta} this period</div>
      )}
      {sub && <div className="text-xs text-white/40 mt-1">{sub}</div>}
    </div>
  );
}
